import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  useConnections,
  useLanding,
  useModels,
  useProviders,
  useRateMessage,
  useRegenerate,
  useSendMessage,
  useStopRun,
  useThread,
  useUpdateThread,
} from "@/api/queries";
import { ApiError } from "@/api/client";
import { useElapsed, useRunStream } from "@/chat/useRunStream";
import { useScrollLink } from "@/chat/useScrollLink";
import { useNewConversation } from "@/chat/newConversation";
import { useWorkspace } from "@/chat/workspace";
import { ArtifactPanel } from "@/components/artifacts/ArtifactPanel";
import { CompactionDivider } from "@/components/chat/CompactionDivider";
import { Composer } from "@/components/chat/Composer";
import { ContextMeter } from "@/components/chat/ContextMeter";
import { AssistantTurn, LiveTurn, UserTurn } from "@/components/chat/MessageTurn";
import { ModelPicker } from "@/components/chat/ModelPicker";
import { SourceBar } from "@/components/chat/SourceBar";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { WorkspaceSplit } from "@/components/chat/WorkspaceSplit";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { EditableTitle } from "@/components/ui/EditableTitle";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { AlertIcon, ChartIcon, ChatIcon, SettingsIcon } from "@/components/icons";
import { traceSources } from "@/chat/trace";
import { cn } from "@/lib/cn";
import { useBrand } from "@/theme/context";

/**
 * The conversation workspace (PRD FR-4, FR-6.7).
 *
 * The run, not the request, is the unit of work here: sending a question
 * returns a run id and the answer arrives over SSE, so a page refresh mid-run
 * reattaches rather than losing it. `activeRunId` is therefore seeded from the
 * thread's own `active_run` — that is what makes reattachment automatic instead
 * of something the user has to trigger.
 *
 * The right-hand pane mirrors *one* answer — the one nearest the top third of
 * the conversation — rather than accumulating every chart of the session. A
 * conversation is a sequence of questions; the workspace is what the current
 * one produced, with its scope and its provenance attached.
 */
export function ChatPage() {
  const params = useParams({ strict: false }) as { threadId?: string };
  const threadId = params.threadId ?? null;
  const { productName } = useBrand();

  const [draft, setDraft] = useState("");
  // Bumped when the artifact pane writes a follow-up into the box (LINK-4).
  const [composerFocus, setComposerFocus] = useState(0);
  const askAbout = useCallback((text: string) => {
    setDraft(text);
    setComposerFocus((count) => count + 1);
  }, []);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  // The source picker and the conversation list live in the sidebar now
  // (backlog item 2); the page reads the same shared answer.
  const {
    sources,
    connectionId,
    select: rememberSource,
    start: newThread,
    creating,
  } = useNewConversation();
  const { data: providers } = useProviders();
  const { data: models } = useModels();
  const { data: allSources } = useConnections();
  const sourceNames = useMemo(
    () => Object.fromEntries((allSources ?? []).map((row) => [row.id, row.name])),
    [allSources],
  );
  const thread = useThread(threadId ?? "");
  const detail = threadId ? thread.data : undefined;

  const updateThread = useUpdateThread(threadId ?? "");
  const sendMessage = useSendMessage(threadId ?? "");
  const regenerate = useRegenerate(threadId ?? "");
  const rate = useRateMessage(threadId ?? "");
  const stopRun = useStopRun();

  const { run, busy } = useRunStream(activeRunId, threadId);
  const elapsed = useElapsed(run.startedAt, busy);
  const { tab, setTab } = useWorkspace();

  // Runs this page watched end. The thread in the cache was fetched while the
  // run was going, so it still names it as `active_run` for as long as the
  // refetch that `done` asked for is in flight. Adopting it from there
  // reattaches to a finished run, whose journal replays straight to `done`,
  // which invalidates the thread again — cancelling the very refetch that
  // would have ended it. Whenever that cycle is quicker than the thread
  // request, it never ends: ~40 requests a second, and an answer that flickers
  // until something else changes the timing.
  const endedRuns = useRef(new Set<string>());

  const catalog = useMemo(() => models ?? [], [models]);
  const noProvider = (providers ?? []).filter((provider) => provider.enabled).length === 0;

  // Reattach to a run that was already in flight when this page loaded —
  // the browser-refresh case (PRD NFR-3).
  useEffect(() => {
    const active = detail?.active_run;
    if (active && active.id !== activeRunId && !endedRuns.current.has(active.id)) {
      setActiveRunId(active.id);
    }
  }, [detail?.active_run, activeRunId]);

  // Opening a conversation adopts its source as the working context.
  //
  // Without this the sidebar's picker kept whatever was chosen last, so "New
  // conversation" read from one source while the conversation on screen was on
  // another — and the click appeared to *switch the data source in the title*
  // rather than start anything. A person has one working context (see
  // `preferredSource`), and the conversation they are looking at is it. An
  // explicit pick still wins: this only fires when the open thread changes.
  useEffect(() => {
    if (detail?.connection_id) rememberSource(detail.connection_id);
  }, [detail?.connection_id, rememberSource]);

  // A run that compacted mid-flight changed the thread underneath us, so the
  // divider has to appear without waiting for the answer to land.
  useEffect(() => {
    if (run.compactedThroughSeq && threadId) void thread.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.compactedThroughSeq, threadId]);

  // A finished run hands the conversation back to the stored messages.
  //
  // A dropped stream is not an ending: the run may well still be going, and
  // the thread's `active_run` is how it gets picked up again. Only an ending
  // the server announced is remembered.
  useEffect(() => {
    if (run.status === "complete" || run.status === "stopped" || run.status === "error") {
      if (activeRunId && run.error?.code !== "stream_lost") endedRuns.current.add(activeRunId);
      setActiveRunId(null);
    }
    // Keyed on the status alone: a new run is adopted while the last one's
    // terminal status is still showing, and re-running this then would end it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run.status]);

  const modelRef = detail?.model_ref ?? catalog[0]?.ref ?? "";
  const messages = useMemo(() => detail?.messages ?? [], [detail?.messages]);
  const visible = useMemo(
    () =>
      messages.filter(
        (message) => message.role !== "assistant" || message.status !== "running" || !busy,
      ),
    [messages, busy],
  );

  const scroller = useRef<HTMLDivElement>(null);
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [visible.length, run.text, run.steps.length]);

  // Which answer the artifact pane mirrors (PRD FR-6.7).
  const answerIds = useMemo(
    () =>
      visible
        .filter((message) => message.role === "assistant" && message.status !== "error")
        .map((message) => message.id),
    [visible],
  );
  const selectedId = useScrollLink(scroller, answerIds);
  const selected = useMemo(
    () => messages.find((message) => message.id === selectedId) ?? null,
    [messages, selectedId],
  );
  const artifactCount = selected?.artifacts?.length ?? 0;

  const landing = useLanding(threadId ?? "", Boolean(threadId) && messages.length === 0);

  // The first turn still sent to the model in full. Everything before it is
  // represented by the pinned briefing (PRD FR-3.4).
  const firstUncompactedSeq = useMemo(() => {
    const through = detail?.compaction?.through_seq ?? 0;
    return visible.find((message) => message.seq > through)?.seq ?? null;
  }, [visible, detail?.compaction?.through_seq]);

  async function ask(text: string) {
    if (!threadId) return;
    setFailure(null);
    setDraft("");
    setTab("conversation");
    try {
      const started = await sendMessage.mutateAsync({ text, model_ref: modelRef || null });
      setActiveRunId(started.run_id);
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : "The question could not be sent.");
      setDraft(text);
    }
  }

  const composerBlocker = noProvider
    ? "Add a model provider in Settings before asking a question."
    : !threadId
      ? "Start a conversation to ask a question."
      : null;

  const conversation = (
    <>
      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-5 py-6">
          {!threadId ? (
            <EmptyState
              icon={<ChatIcon className="size-6" />}
              title={`Ask ${productName} about your data`}
              detail="Pick a source and start a conversation. Every answer arrives with the SQL that produced it."
              action={
                sources.length ? (
                  <Button
                    variant="primary"
                    loading={creating}
                    onClick={() => void newThread(connectionId)}
                  >
                    New conversation
                  </Button>
                ) : (
                  <Link to="/sources" className="text-accent hover:underline">
                    Add a data source first
                  </Link>
                )
              }
            />
          ) : null}

          {threadId && messages.length === 0 && landing.data ? (
            <div className="flex flex-col gap-4">
              <div>
                <h2 className="type-h3 text-ink">{landing.data.greeting}</h2>
                <p className="type-support text-ink-secondary mt-1">
                  {landing.data.relation_count} relations
                  {landing.data.documented ? "" : " · undocumented source"}
                </p>
              </div>
              {!landing.data.documented ? (
                <p className="bg-warning-subtle text-warning type-support rounded-element px-3 py-2">
                  This source has no comments and no data dictionary, so answers rest on column
                  names alone.{" "}
                  <Link
                    to="/sources/$connectionId"
                    params={{ connectionId: detail?.connection_id ?? connectionId }}
                    search={{ tab: "knowledge" as const }}
                    className="underline"
                  >
                    Write one
                  </Link>{" "}
                  to make it reliable.
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {landing.data.suggestions.map((suggestion) => (
                  <Button
                    key={suggestion.label}
                    size="sm"
                    variant="secondary"
                    disabled={Boolean(composerBlocker)}
                    onClick={() => void ask(suggestion.question)}
                  >
                    {suggestion.label}
                  </Button>
                ))}
              </div>
            </div>
          ) : null}

          {visible.map((message) => (
            <div key={message.id} className="flex flex-col gap-6">
              {/* The divider sits *before* the first turn the model still sees
                  in full, so everything above it is visibly the part the
                  briefing now stands for (PRD FR-3.4). */}
              {detail?.compaction && message.seq === firstUncompactedSeq ? (
                <CompactionDivider compaction={detail.compaction} />
              ) : null}
              {message.role === "user" ? (
                <UserTurn text={message.text} />
              ) : (
              <AssistantTurn
                message={message}
                connectionId={detail?.connection_id}
                sourceNames={sourceNames}
                selected={message.id === selectedId && answerIds.length > 1}
                onAsk={(question) => void ask(question)}
                onRegenerate={() => {
                  regenerate.mutate(
                    { model_ref: modelRef || null },
                    { onSuccess: (started) => setActiveRunId(started.run_id) },
                  );
                }}
                onRate={(value) => rate.mutate({ messageId: message.id, value })}
                canRegenerate={!busy && message.seq === messages[messages.length - 1]?.seq}
              />
              )}
            </div>
          ))}

          {busy ? (
            <LiveTurn
              run={run}
              elapsed={elapsed}
              connectionId={detail?.connection_id}
              sources={traceSources(detail?.connection_ids ?? [], sourceNames)}
            />
          ) : null}

          {thread.isLoading && threadId ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : null}

          <div ref={bottom} />
        </div>
      </div>

      <div className="border-border border-t px-5 py-4">
        <div className="mx-auto w-full max-w-3xl">
          {failure ? (
            <p className="bg-error-subtle text-error type-support rounded-element mb-2 px-3 py-2">
              {failure}
            </p>
          ) : null}
          <Composer
            value={draft}
            onChange={setDraft}
            focusSignal={composerFocus}
            onSend={(text) => void ask(text)}
            onStop={() => {
              if (activeRunId) stopRun.mutate(activeRunId);
            }}
            stopping={stopRun.isPending}
            busy={busy}
            disabled={composerBlocker}
            placeholder={
              (detail?.connection_ids?.length ?? 0) > 1
                ? "Ask a question across these sources…"
                : "Ask a question about this data source…"
            }
          />
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
            <Chip tone="neutral">read-only SQL</Chip>
            {threadId ? (
              <ContextMeter
                threadId={threadId}
                draft={draft}
                modelRef={modelRef}
                busy={busy}
              />
            ) : (
              <span className="type-caption text-ink-disabled">
                Every answer carries the queries that produced it.
              </span>
            )}
            <span className="flex-1" />
            <Link
              to="/settings/providers"
              className="type-caption text-ink-disabled hover:text-ink inline-flex items-center gap-1"
            >
              <SettingsIcon className="size-3" />
              Providers
            </Link>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="flex h-full min-h-0">
      <section className="flex min-w-0 flex-1 flex-col">
        <header className="border-border flex min-h-16 flex-wrap items-center gap-3 border-b px-5 py-3">
          <div className="min-w-0 flex-1">
            {threadId ? (
              <EditableTitle
                as="h1"
                className="type-h4 text-ink"
                value={detail?.title ?? ""}
                placeholder="New conversation"
                label="conversation"
                onSave={(title) => updateThread.mutateAsync({ title })}
              />
            ) : (
              <h1 className="type-h4 text-ink truncate">New conversation</h1>
            )}
            {threadId && detail?.connection_ids?.length ? (
              <SourceBar threadId={threadId} connectionIds={detail.connection_ids} />
            ) : null}
          </div>

          {/* A conversation prints too (PRD FR-9.2): turns, artifacts and,
              optionally, every statement they ran. */}
          {threadId ? <ExportMenu base={`/api/threads/${threadId}`} label="Export" /> : null}

          {/* Below the split's breakpoint the two panes are tabs (FR-6.7). */}
          <div
            className="border-border rounded-element flex items-center gap-0.5 border p-0.5 lg:hidden"
            role="tablist"
            aria-label="Workspace"
          >
            {(["conversation", "artifacts"] as const).map((key) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={cn(
                  "type-caption rounded-inner motion-fast inline-flex items-center gap-1 px-2 py-1",
                  tab === key ? "bg-selected text-ink" : "text-ink-secondary hover:bg-hover",
                )}
              >
                {key === "artifacts" ? <ChartIcon className="size-3" /> : null}
                {key}
                {key === "artifacts" && artifactCount ? ` (${artifactCount})` : ""}
              </button>
            ))}
          </div>

          <ModelPicker
            models={catalog}
            value={modelRef}
            disabled={busy || !threadId}
            onChange={(ref) => {
              if (threadId) updateThread.mutate({ model_ref: ref });
            }}
          />
        </header>

        {noProvider ? (
          <div className="border-border border-b px-5 py-3">
            <p className="bg-warning-subtle text-warning type-support rounded-element flex flex-wrap items-center gap-2 px-3 py-2">
              <AlertIcon className="size-3.5" />
              No model provider is configured yet.
              <Link to="/settings/providers" className="underline">
                Add one
              </Link>
              to start asking questions.
            </p>
          </div>
        ) : null}

        <WorkspaceSplit
          conversation={conversation}
          artifacts={
            <ArtifactPanel
              message={selected}
              threadId={threadId ?? ""}
              busy={busy}
              onAsk={askAbout}
            />
          }
        />
      </section>
    </div>
  );
}
